  /**
   * Energized Electrical Work Permit Component
   * ===============================
   * 
   * Individual form component for energized electrical work permit submissions.
   * 
   * Base Components:
   * - EXTENDS BaseFormComponent<EnergizedElecWorkPermit> for form logic inheritance
   * - USES FormLayoutComponent for step navigation UI (in template)
   * 
   * Form Configuration:
   * - Form structure, steps, sections, and field organization are defined in the form model
   * - Signature and upload requirements are configured via form model metadata
   * - Component implements abstract methods: initializeForm() and getFormData()
   * - Component overrides setupDefaultStepValidation() for custom step validation rules
   * 
   * Key Functionality:
   * - Creates nested form groups for logical organization (basic, requester, hazards, qualifiedPerson, ppe, approvals)
   * - Implements checkbox groups for safety requirements and PPE with dynamic label mapping
   * - Provides form validation for electrical work safety compliance
   * - Implements step validation with signature requirements for final step
   * - Flattens nested form structure for backend submission
   * 
   * Form Model vs Component Responsibilities:
   * - FORM MODEL: Defines step/section structure, field mappings, attachment requirements, and form metadata
   * - COMPONENT: Handles Angular form validation, form group creation, conditional field logic, and overrides base form methods
   * 
   * @author MacSafety Development Team
   * @version 1.0
   */


import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { BaseFormComponent } from '../../base-form.component';
import { FormStep } from '../../form-layout.component';
import { EnergizedElecWorkPermit } from '../../../../../models/forms/energized-elec-work-permit/energized-elec-work-permit.model';
import { FormService } from '../../../../../services/form.service';
import { EntraService } from '../../../../../services/entra.service';
import { AttachmentUploadService } from '../../../../../services/attachment-upload.service';
import { SignatureService } from '../../../../../services/signature.service';
import { ToastService } from '../../../../../services/toast.service';
import { ScreenSizeService } from '../../../../../services/screen-size.service';
import { FormModelService } from '../../../../../services/form-model.service';
import { checkboxValidator } from '../../../../../utils/form-validators';

/**
 * Component for handling Energized Electrical Work Permit submissions.
 * This form is used to document and authorize work on energized electrical equipment,
 * ensuring proper safety measures are in place.
 */
@Component({
  selector: 'app-energized-elec-work-permit',
  templateUrl: './energized-elec-work-permit.component.html',
  styleUrls: ['./energized-elec-work-permit.component.css']
})
export class EnergizedElecWorkPermitComponent extends BaseFormComponent<EnergizedElecWorkPermit> implements OnInit {
  // Form type identifier used for routing and form metadata lookup
  override formType = EnergizedElecWorkPermit.getFormInfo().formType;
  
  // Instance of the form data model
  override model = new EnergizedElecWorkPermit();
  
  // Define the form steps and their properties
  override steps: FormStep[] = EnergizedElecWorkPermit.getFormSteps();

  checkWhenCompleteCheckboxes: { name: string, label: string }[] = [];
  ppeCheckboxes: { name: string, label: string }[] = [];

  constructor(
    protected override fb: FormBuilder,
    protected override formService: FormService,
    protected override router: Router,
    protected override entraService: EntraService,
    protected override attachmentService: AttachmentUploadService,
    protected override toastService: ToastService,
    protected override signatureService: SignatureService,
    protected override screenSizeService: ScreenSizeService,
    protected formModelService: FormModelService
  ) {
    super(fb, formService, router, entraService, attachmentService, toastService, signatureService, screenSizeService);
    
    // Initialize form metadata first
    this.formMetadata = this.formModelService.getFormMetadata(this.formType)!;
    
 

  
  }

  override ngOnInit(): void {
    super.ngOnInit();
  }

  /**
   * Initializes the form with its controls and validators.
   * Sets up the form group with all required fields and their validation rules.
   */
  protected override initializeForm(): void {
    this.checkWhenCompleteCheckboxes = this.getCheckWhenCompleteCheckboxes();
    this.ppeCheckboxes = this.getPpeCheckboxes();

    // Create form group for checkWhenComplete
    const checkWhenCompleteGroup = this.fb.group({});
    this.checkWhenCompleteCheckboxes.forEach(checkbox => {
      checkWhenCompleteGroup.addControl(checkbox.name, this.fb.control(false));
    });

    const ppecheckFormGroup = this.fb.group({});
    this.ppeCheckboxes.forEach(checkbox => {
      ppecheckFormGroup.addControl(checkbox.name, this.fb.control(false));
    });




    this.form = this.fb.group({
      basic: this.fb.group({
        jobWorkOrderName: [this.model.jobWorkOrderName],
        jobWorkOrderNumber: [this.model.jobWorkOrderNumber],
      }),
      requester: this.fb.group({
        circuitEquipmentDescription: [this.model.circuitEquipmentDescription],
        upstreamOverCurrentDeviceLocation: [this.model.upstreamOverCurrentDeviceLocation],
        workDescription: [this.model.workDescription],
        justificationForEnergizedWork: [this.model.justificationForEnergizedWork],
        requesterTitle: [this.model.requesterTitle],
        requesterDate: [this.model.requesterDate],
      }),
      hazards: this.fb.group({
        electricalHazards: [this.model.electricalHazards],
        mechanicalHazards: [this.model.mechanicalHazards],
        workingSpaceConstraints: [this.model.workingSpaceConstraints],
        otherHazards: [this.model.otherHazards]
      }),
      qualifiedPerson: this.fb.group({
        checkWhenComplete: checkWhenCompleteGroup,
        agreeWorkCanBeDoneSafely: [this.model.agreeWorkCanBeDoneSafely],
      }),
      ppe: this.fb.group({
        ppeCheckboxes: ppecheckFormGroup,
        otherPPEDescription: [this.model.otherPPEDescription]
      }),
      approvals: this.fb.group({
        electricianName: [this.model.electricianName],
        electricianSignature: [this.model.electricianSignature],
        electricianSignatureDate: [this.model.electricianSignatureDate],
        supervisorName: [this.model.supervisorName],
        supervisorSignature: [this.model.supervisorSignature],
        supervisorSignatureDate: [this.model.supervisorSignatureDate]
      })
    });
  }

    // Checkbox data getters
    getPpeCheckboxes(): { name: string, label: string }[] {
    
        return [
          { name: 'safetyGlasses', label: this.fieldMappings['safetyGlasses'] },
          { name: 'insulatedTools', label: this.fieldMappings['insulatedTools'] },
          { name: 'eightCalBalaclava', label: this.fieldMappings['eightCalBalaclava'] },
          { name: 'eightCalRubberGloves', label: this.fieldMappings['eightCalRubberGloves'] },
          { name: 'eightCalFaceShield', label: this.fieldMappings['eightCalFaceShield'] },
          { name: 'eightCalFireResistanceClothing', label: this.fieldMappings['eightCalFireResistanceClothing'] },
          { name: 'eightCalFlashSuit', label: this.fieldMappings['eightCalFlashSuit'] },
          { name: 'fortyCalRubberGloves', label: this.fieldMappings['fortyCalRubberGloves'] },
          { name: 'fortyCalFaceShield', label: this.fieldMappings['fortyCalFaceShield'] },
          { name: 'fortyCalFlashSuit', label: this.fieldMappings['fortyCalFlashSuit'] },
          { name: 'otherPPE', label: this.fieldMappings['otherPPE'] }
        ];
 
    }
  
    getCheckWhenCompleteCheckboxes(): { name: string, label: string }[] {
    //  console.log('fieldmappings in getters', this.fieldMappings);
      return [
          { name: 'detailedJobDescriptionProcedure', label: this.fieldMappings['detailedJobDescriptionProcedure'] },
          { name: 'safeWorkPracticesDescription', label: this.fieldMappings['safeWorkPracticesDescription'] },
          { name: 'shockHazardAnalysisDescription', label: this.fieldMappings['shockHazardAnalysisDescription'] },
          { name: 'shockProtectionBoundariesDetermination', label: this.fieldMappings['shockProtectionBoundariesDetermination'] },
          { name: 'arcFlashHazardAnalysisResults', label: this.fieldMappings['arcFlashHazardAnalysisResults'] },
          { name: 'arcFlashProtectionBoundaryDetermination', label: this.fieldMappings['arcFlashProtectionBoundaryDetermination'] },
          { name: 'necessaryPPEForTask', label: this.fieldMappings['necessaryPPEForTask'] },
          { name: 'unqualifiedPersonsAccessRestriction', label: this.fieldMappings['unqualifiedPersonsAccessRestriction'] },
          { name: 'jobBriefingCompletionEvidence', label: this.fieldMappings['jobBriefingCompletionEvidence'] }
        ];
      }
     
  
    // Form group getters
    get basicFormGroup(): FormGroup {

      return this.form.get('basic') as FormGroup;
    }
  
    get requesterFormGroup(): FormGroup {
      return this.form.get('requester') as FormGroup;
    }
  
    get hazardsFormGroup(): FormGroup {
      return this.form.get('hazards') as FormGroup;
    }
  
    get qualifiedPersonFormGroup(): FormGroup {
      return this.form.get('qualifiedPerson') as FormGroup;
    }
  
    get checkWhenCompleteFormGroup(): FormGroup {
      return this.qualifiedPersonFormGroup.get('checkWhenComplete') as FormGroup;
    }
  
    get ppeFormGroup(): FormGroup {
      return this.form.get('ppe') as FormGroup;
    }

    get ppeCheckboxesFormGroup(): FormGroup {
      return this.ppeFormGroup.get('ppeCheckboxes') as FormGroup;
    }
  
    get approvalsFormGroup(): FormGroup {
      return this.form.get('approvals') as FormGroup;
    }

  /**
   * Retrieves the current form data and creates a new EnergizedElecWorkPermit instance.
   * This method is called when submitting the form.
   */
  protected override getFormData(): EnergizedElecWorkPermit {
    const formValue = this.form.value;
    
    // Flatten the nested form groups into a single object
    const flattenedData = {
      // Basic info
      ...formValue.basic,
      
      // Requester info
      ...formValue.requester,
      
      // Hazards (checkbox group)
      ...formValue.hazards,
      
      // Qualified person info and checkboxes
      ...formValue.qualifiedPerson.checkWhenComplete,
      agreeWorkCanBeDoneSafely: formValue.qualifiedPerson.agreeWorkCanBeDoneSafely,
      
      // PPE (checkbox group)
      ...formValue.ppe.ppeCheckboxes,
      otherPPEDescription: formValue.ppe.otherPPEDescription,
      
      // Approvals
      ...formValue.approvals
    };
    
    const energizedElecWorkPermit = new EnergizedElecWorkPermit(flattenedData);
    const updatedEnergizedElecWorkPermit = this.updateModelWithAttachments(energizedElecWorkPermit);
    return updatedEnergizedElecWorkPermit;
  }

  /**
   * Sets up validation rules for each step of the energized electrical work permit form.
   * Each step has specific validation requirements that must be met before proceeding:
   * - Basic: Requires date and signature validation
   * 
   * The validation ensures that:
   * 1. All required fields are filled out
   * 2. Required signatures are collected
   * 3. Form data meets safety and compliance requirements
   */
   protected override setupDefaultStepValidation(): void {
    // Skip validation setup if noFormRequirements is true
    if (!this.shouldApplyFormRequirements()) {
      return;
    }
    // Define step indices
    const SIGNATURE_REQUIREMENT_INDEX: number = 0;

    // Custom validation rules for each step using the form groups directly
    this.setupStepValidation('basic', this.basicFormGroup);
    this.setupStepValidation('requester', this.requesterFormGroup);
    this.setupStepValidation('hazards', this.hazardsFormGroup);
    this.setupStepValidation('qualified-person', this.qualifiedPersonFormGroup);
    this.setupStepValidation('ppe', this.ppeFormGroup);
    this.setupStepValidation('approvals', this.approvalsFormGroup);
  }
}